import { ref, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { LoadingStateModel } from '@/models';
import type { PerfusionScanModel } from '@/models/perfusionScan';
import PerfusionScanService from '@/services/perfusionScanService';

type PerfusionScanStoreShape = {
  scans: Ref<PerfusionScanModel[]>;
  total: Ref<number>;
  scansLoader: Ref<LoadingStateModel>;
  getScans: (pumpCenterId: string, perfusionId: string) => Promise<void>;
  clearScans: () => void;
};

export const usePerfusionScanStore = defineStore('perfusionScan', (): PerfusionScanStoreShape => {
  const scans = ref<PerfusionScanModel[]>([]);
  const total = ref(0);
  const scansLoader = ref(new LoadingStateModel({ isLoading: false }));
  // Guards against a slower request for a previous case overwriting the current one's scans.
  let activeScansRequestId = 0;

  const getScans = async (pumpCenterId: string, perfusionId: string) => {
    const requestId = ++activeScansRequestId;
    scansLoader.value.startLoading();
    try {
      const fetched = await PerfusionScanService.getScans(pumpCenterId, perfusionId);
      if (requestId !== activeScansRequestId) return;
      scans.value = fetched.scans;
      total.value = fetched.total;
    } catch (error) {
      if (requestId !== activeScansRequestId) return;
      scans.value = [];
      total.value = 0;
      scansLoader.value.setError(error);
    } finally {
      if (requestId === activeScansRequestId) {
        scansLoader.value.stopLoadingAndFinish();
      }
    }
  };

  const clearScans = () => {
    activeScansRequestId++;
    scans.value = [];
    total.value = 0;
    // A request abandoned by the bump above skips its own `finally`, so the loader is reset here.
    // Left loading, it strands the panel on a spinner the next time it opens without a load
    // starting. Reset rather than "finished": nothing completed, the store is simply empty again.
    scansLoader.value.setState({ isLoading: false });
  };

  return {
    scans,
    total,
    scansLoader,
    getScans,
    clearScans
  };
});
